import {getGeolocationPlatform} from "@/src/modules/core/lib/geolocation-platform";

/** W3C fill-light modes for ImageCapture.takePhoto() — torch is separate (MediaStreamTrack). */
export type FillLightMode = "auto" | "off" | "on" | "flash";

export type CameraFacingMode = "environment" | "user";

/** Samsung / Android Chrome often omits torch in getCapabilities() but still honors applyConstraints. */
export const ANDROID_REAR_OPTIMISTIC_FLASH_SUPPORT: CameraFlashSupport = {
  torch: true,
  flashOnCapture: true,
  fillLightModes: [],
};

export type CameraFlashSupport = {
  /** Continuous light during preview via track.applyConstraints({ torch }). */
  torch: boolean;
  /** Strobe on shutter via ImageCapture.takePhoto({ fillLightMode: "flash" }). */
  flashOnCapture: boolean;
  /** Modes reported by ImageCapture.getPhotoCapabilities() when probed. */
  fillLightModes: FillLightMode[];
};

type MediaTrackCapabilitiesWithTorch = MediaTrackCapabilities & {
  torch?: boolean;
};

type PhotoCapabilitiesWithFillLight = {
  fillLightMode?: FillLightMode[];
};

type ImageCaptureWithPhotoCaps = {
  getPhotoCapabilities: () => Promise<PhotoCapabilitiesWithFillLight>;
};

type ImageCaptureConstructor = new (track: MediaStreamTrack) => ImageCaptureWithPhotoCaps;

function getImageCaptureConstructor(): ImageCaptureConstructor | null {
  const ctor = (globalThis as {ImageCapture?: ImageCaptureConstructor}).ImageCapture;
  return ctor ?? null;
}

function capabilityTorchSupported(torch: MediaTrackCapabilitiesWithTorch["torch"]): boolean {
  if (torch === true) {
    return true;
  }
  // Chromium on some Android builds reports torch as [false, true].
  if (Array.isArray(torch)) {
    return torch.includes(true);
  }
  return false;
}

function trackSupportsTorch(track: MediaStreamTrack): boolean {
  const caps = track.getCapabilities?.() as MediaTrackCapabilitiesWithTorch | undefined;
  if (!caps || !("torch" in caps)) {
    return false;
  }
  return capabilityTorchSupported(caps.torch);
}

async function probeFillLightModes(track: MediaStreamTrack): Promise<FillLightMode[]> {
  const ImageCapture = getImageCaptureConstructor();
  if (!ImageCapture) {
    return [];
  }

  try {
    const imageCapture = new ImageCapture(track);
    const photoCaps = await imageCapture.getPhotoCapabilities();
    return photoCaps?.fillLightMode ?? [];
  } catch {
    return [];
  }
}

function hasCaptureFillLight(modes: FillLightMode[]): boolean {
  return modes.includes("flash") || modes.includes("on");
}

/** Returns which flash modes the active rear/environment track exposes (if any). */
export async function getCameraFlashSupport(
  track: MediaStreamTrack | null | undefined,
): Promise<CameraFlashSupport> {
  if (!track || track.readyState !== "live") {
    return {torch: false, flashOnCapture: false, fillLightModes: []};
  }

  const [torch, fillLightModes] = await Promise.all([
    Promise.resolve(trackSupportsTorch(track)),
    probeFillLightModes(track),
  ]);

  return {
    torch,
    flashOnCapture: hasCaptureFillLight(fillLightModes),
    fillLightModes,
  };
}

function mergeFlashSupport(
  current: CameraFlashSupport,
  next: CameraFlashSupport,
): CameraFlashSupport {
  const fillLightModes = [...new Set([...current.fillLightModes, ...next.fillLightModes])];
  return {
    torch: current.torch || next.torch,
    flashOnCapture: current.flashOnCapture || next.flashOnCapture,
    fillLightModes,
  };
}

export type CameraFlashProbeResult = {
  support: CameraFlashSupport;
  /** Number of probe rounds completed (1–3). */
  attempts: number;
};

/** Re-probe after streaming — Samsung builds may report torch only after warm-up. */
export async function getCameraFlashSupportWithRetry(
  track: MediaStreamTrack,
): Promise<CameraFlashProbeResult> {
  const retryDelaysMs = [0, 400, 800];
  let merged: CameraFlashSupport = {torch: false, flashOnCapture: false, fillLightModes: []};
  let attempts = 0;

  for (const delayMs of retryDelaysMs) {
    if (delayMs > 0) {
      await new Promise((resolve) => window.setTimeout(resolve, delayMs));
    }
    if (track.readyState !== "live") {
      break;
    }
    attempts += 1;
    merged = mergeFlashSupport(merged, await getCameraFlashSupport(track));
    if (isCameraFlashAvailable(merged)) {
      break;
    }
  }

  return {support: merged, attempts: attempts || 1};
}

export function isCameraFlashAvailable(support: CameraFlashSupport): boolean {
  return support.torch || support.flashOnCapture;
}

export function isAndroidRearCameraFlashOptimistic(facingMode: CameraFacingMode): boolean {
  return getGeolocationPlatform() === "android" && facingMode === "environment";
}

/** Show flash control when probed support exists, or Android rear (hardware always present). */
export function shouldShowCameraFlashControl(
  support: CameraFlashSupport | null,
  facingMode: CameraFacingMode,
): boolean {
  if (support && isCameraFlashAvailable(support)) {
    return true;
  }
  return isAndroidRearCameraFlashOptimistic(facingMode);
}

/** Merge probed caps with Android rear optimistic fallback for toggle/capture. */
export function resolveCameraFlashSupport(
  probed: CameraFlashSupport | null,
  facingMode: CameraFacingMode,
): CameraFlashSupport {
  if (probed && isCameraFlashAvailable(probed)) {
    return probed;
  }
  if (isAndroidRearCameraFlashOptimistic(facingMode)) {
    return ANDROID_REAR_OPTIMISTIC_FLASH_SUPPORT;
  }
  return probed ?? {torch: false, flashOnCapture: false, fillLightModes: []};
}

function readTorchSetting(track: MediaStreamTrack): boolean | undefined {
  const settings = track.getSettings?.() as {torch?: boolean} | undefined;
  return settings?.torch;
}

function torchApplySucceeded(track: MediaStreamTrack, enabled: boolean): boolean {
  const actual = readTorchSetting(track);
  if (enabled) {
    if (actual === true) {
      return true;
    }
    if (actual === false) {
      return false;
    }
    // Some Samsung builds omit torch from getSettings() even when the LED is on.
    if (trackSupportsTorch(track)) {
      return true;
    }
    return false;
  }
  return actual !== true;
}

/** Turn continuous torch on/off. No-op when unsupported unless optimistic (Android rear). */
export async function setCameraTorch(
  track: MediaStreamTrack | null | undefined,
  enabled: boolean,
  options: {optimistic?: boolean} = {},
): Promise<boolean> {
  if (!track || track.readyState !== "live") {
    return false;
  }

  if (!trackSupportsTorch(track) && !options.optimistic) {
    return false;
  }

  const attempts: MediaTrackConstraints[] = [
    {torch: enabled} as MediaTrackConstraints,
    {advanced: [{torch: enabled} as MediaTrackConstraintSet]} as MediaTrackConstraints,
    {
      advanced: [{torch: enabled} as MediaTrackConstraintSet],
      torch: enabled,
    } as MediaTrackConstraints,
  ];

  for (const constraints of attempts) {
    try {
      await track.applyConstraints(constraints);
      if (torchApplySucceeded(track, enabled)) {
        return true;
      }
    } catch {
      // try next constraint shape
    }
  }

  return false;
}

/**
 * Samsung multi-lens rear cameras often bind an ultra-wide track without torch.
 * Probe other video inputs and return the first deviceId that reports torch support.
 */
export async function findTorchCapableDeviceId(
  currentDeviceId?: string,
): Promise<string | null> {
  const enumerate = navigator.mediaDevices?.enumerateDevices?.bind(navigator.mediaDevices);
  const getUserMedia = navigator.mediaDevices?.getUserMedia?.bind(navigator.mediaDevices);
  if (!enumerate || !getUserMedia) {
    return null;
  }

  const devices = await enumerate();
  const videoInputs = devices.filter(
    (device) => device.kind === "videoinput" && device.deviceId.length > 0,
  );

  for (const device of videoInputs) {
    if (device.deviceId === currentDeviceId) {
      continue;
    }

    let probeStream: MediaStream | null = null;
    try {
      probeStream = await getUserMedia({
        video: {deviceId: {exact: device.deviceId}},
        audio: false,
      });
      const track = probeStream.getVideoTracks()[0];
      if (track && trackSupportsTorch(track)) {
        return device.deviceId;
      }
    } catch {
      // device busy or unsupported — try next lens
    } finally {
      probeStream?.getTracks().forEach((track) => {
        if (track.readyState !== "ended") {
          track.stop();
        }
      });
    }
  }

  return null;
}

export function requestCameraStreamForDevice(
  deviceId: string,
  facingMode: CameraFacingMode = "environment",
): Promise<MediaStream> | null {
  const getUserMedia = navigator.mediaDevices?.getUserMedia?.bind(navigator.mediaDevices);
  if (!getUserMedia) {
    return null;
  }

  return getUserMedia({
    video: {
      deviceId: {exact: deviceId},
      facingMode: {ideal: facingMode},
      width: {ideal: 1920},
      height: {ideal: 1080},
    },
    audio: false,
  });
}

/** Best-effort torch off before stopping tracks (OS LED). */
export async function turnOffCameraTorch(
  stream: MediaStream | null | undefined,
): Promise<void> {
  const track = stream?.getVideoTracks()[0];
  if (!track) {
    return;
  }
  await setCameraTorch(track, false);
}

export function getFillLightModeForCapture(
  flashEnabled: boolean,
  probedSupport: CameraFlashSupport | null | undefined,
): FillLightMode | undefined {
  if (!flashEnabled || !probedSupport?.flashOnCapture) {
    return undefined;
  }

  const modes = probedSupport.fillLightModes;
  if (modes.includes("flash")) {
    return "flash";
  }
  if (modes.includes("on")) {
    return "on";
  }
  return undefined;
}
